// frontend/src/api/draftApi.js
// Draft-related API calls extracted from DatabaseAPI.js
// Phase 1 Platform Extraction — Step 15B

import { API_BASE, getCsrfHeaders } from './transport';

export function getDraftableLeagues() {
    console.log(`DatabaseAPI: Getting available leagues for drafting`);

    return fetch(`${API_BASE}/v2/leagues/draft-team/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get draftable leagues response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get draftable leagues failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get draftable leagues`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get draftable leagues successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting draftable leagues:', error);
        throw error;
    });
}

export function draftTeam(leagueData) {
    console.log(`DatabaseAPI: Drafting team in league: ${leagueData.league_id}`);

    return fetch(`${API_BASE}/v2/leagues/draft-team/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(leagueData)
    })
    .then(response => {
        console.log(`DatabaseAPI: Draft team response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Draft team failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to draft team`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Draft team successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error drafting team:', error);
        throw error;
    });
}

export function draftTeamWithTemplate(leagueData, templateId) {
    console.log(`🔍 DatabaseAPI: draftTeamWithTemplate called with template_id: ${templateId}`, leagueData);

    // VALIDATE REQUIRED TEMPLATE ID - ERROR LOUDLY IF MISSING
    if (!templateId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: template_id is required for template-based league joining');
    }

    return fetch(`${API_BASE}/v2/leagues/draft-team/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            ...leagueData,
            template_id: templateId  // REQUIRED: Template selection
        })
    })
    .then(response => {
        console.log(`DatabaseAPI: Draft team with template response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`❌ DatabaseAPI: Draft team with template failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to draft team with template`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`✅ DatabaseAPI: Draft team with template successful:`, data);

        // VALIDATE EXPECTED RESPONSE FORMAT - NO FALLBACKS
        const requiredFields = ['status', 'team_id', 'team_name', 'league_id', 'league_name', 'template_name'];
        requiredFields.forEach(field => {
            if (data[field] === undefined) {
                throw new Error(`❌ INVALID RESPONSE: Missing required field "${field}" in draft team response`);
            }
        });

        return data;
    })
    .catch(error => {
        console.error('❌ DatabaseAPI: Error drafting team with template:', error);
        throw error;
    });
}

export function initializeDraft(teamId, catalogName = 'Standard Draft 2025') {
    console.log(`DatabaseAPI: Initializing draft for team ${teamId} with catalog ${catalogName}`);

    return fetch(`${API_BASE}/v2/draft/start/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            team_id: teamId,
            catalog_name: catalogName
        })
    })
    .then(response => {
        console.log(`DatabaseAPI: Initialize draft response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Initialize draft failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to initialize draft`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Initialize draft successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error initializing draft:', error);
        throw error;
    });
}

export function getNextPack(teamId) {
    console.log(`DatabaseAPI: Getting next pack for team ${teamId}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/next-pack/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get next pack response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get next pack failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get next pack`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get next pack successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting next pack:', error);
        throw error;
    });
}

export function selectFromPack(teamId, packNumber, selectionData) {
    let requestData = {
        team_id: teamId,
        pack_number: packNumber
    };

    // Handle both legacy and dynamic selection formats
    if (typeof selectionData === 'string') {
        // Legacy format: just card type
        requestData.card_type = selectionData;
        console.log(`DatabaseAPI: Selecting ${selectionData} from pack ${packNumber} for team ${teamId} (LEGACY)`);
    } else if (typeof selectionData === 'object' && selectionData !== null) {
        // Dynamic format: selection object with slot number
        requestData = { ...requestData, ...selectionData };
        console.log(`DatabaseAPI: Selecting from pack ${packNumber} for team ${teamId} (DYNAMIC):`, selectionData);
    } else {
        throw new Error('Invalid selection data format - must be string (legacy) or object (dynamic)');
    }

    return fetch(`${API_BASE}/v2/draft/select/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            // getCsrfHeaders() omits the header entirely when no cookie exists,
            // rather than sending the literal `X-CSRFToken: null` that the
            // previous getCsrfToken() call produced.
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
    })
    .then(response => {
        console.log(`DatabaseAPI: Select from pack response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Select from pack failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to select from pack`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Select from pack successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error selecting from pack:', error);
        throw error;
    });
}

export function getDraftStatus(teamId) {
    console.log(`DatabaseAPI: Getting draft status for team ${teamId}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/draft-status/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get draft status response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get draft status failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get draft status`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get draft status successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting draft status:', error);
        throw error;
    });
}

export function autodraftSecureTeam(teamId, catalogName = null) {
    console.log(`DatabaseAPI: Autodrafting team ${teamId} using secure pack system`);

    const requestBody = { team_id: teamId };
    if (catalogName) {
        requestBody.catalog_name = catalogName;
    }

    return fetch(`${API_BASE}/v2/autodraft/team/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
    })
    .then(response => {
        console.log(`DatabaseAPI: Autodraft secure team response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Autodraft secure team failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to autodraft team`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Autodraft secure team successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error autodrafting secure team:', error);
        throw error;
    });
}

export function getPackPreview(teamId, packNumber) {
    console.log(`DatabaseAPI: Getting pack preview for team ${teamId}, pack ${packNumber}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/pack/${packNumber}/preview/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get pack preview response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get pack preview failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get pack preview`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get pack preview successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting pack preview:', error);
        throw error;
    });
}

export function getAvailablePackChoices(teamId) {
    console.log(`DatabaseAPI: Getting available pack choices for team ${teamId}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/available-packs/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get available pack choices response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get available pack choices failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get available pack choices`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get available pack choices successful:`, data);

        // Validate response structure
        if (!data.available_packs || !Array.isArray(data.available_packs)) {
            throw new Error("❌ INVALID RESPONSE: available_packs array is missing or invalid");
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting available pack choices:', error);
        throw error;
    });
}

export function selectPackChoice(teamId, packConfigId, sequenceId = null) {
    console.log(`DatabaseAPI: Selecting pack choice ${packConfigId} for team ${teamId}`);

    const requestBody = {
        pack_config_id: packConfigId
    };

    if (sequenceId) {
        requestBody.sequence_id = sequenceId;
    }

    return fetch(`${API_BASE}/v2/teams/${teamId}/select-pack/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
    })
    .then(response => {
        console.log(`DatabaseAPI: Select pack choice response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Select pack choice failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to select pack choice`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Select pack choice successful:`, data);

        // Validate response structure
        if (!data.success || !data.pack) {
            throw new Error("❌ INVALID RESPONSE: Pack selection response missing required data");
        }

        // Validate pack structure
        if (!data.pack.cards || !Array.isArray(data.pack.cards)) {
            throw new Error("❌ INVALID PACK DATA: Pack missing cards array");
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error selecting pack choice:', error);
        throw error;
    });
}

export function getDraftPoolPlayers(teamId, position) {
    console.log(`DatabaseAPI: Getting draft pool players for team ${teamId}, position ${position}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/draft-pool/?position=${position}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get draft pool players response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get draft pool players failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get draft pool players`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get draft pool players successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting draft pool players:', error);
        throw error;
    });
}

export function getDraftPoolGear(teamId) {
    console.log(`DatabaseAPI: Getting draft pool gear for team ${teamId}`);

    return fetch(`${API_BASE}/v2/teams/${teamId}/draft-pool-gear/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get draft pool gear response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get draft pool gear failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to get draft pool gear`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Get draft pool gear successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting draft pool gear:', error);
        throw error;
    });
}
